'use client';

import useFormEditPage from '@/hooks/use-form-edit-page';
import { Check, MapPin, Search } from 'lucide-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import FormWidget from '@/components/form-widget';
import { TemplateColor } from '@/components/template-page-layout/template-page-layout.types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { KeyboardKeys } from '@/shared/utils/navigation';

import { cn } from '@/utils/styles';

import { useFormLocations } from '../../hooks/use-form-locations';
import { useLocationSearch } from '../../hooks/use-location-search';

type LocationWidgetPopoverProps = {
	text: string;
	color: TemplateColor;
};

export default function LocationWidgetPopover({ text, color }: LocationWidgetPopoverProps) {
	const { isStaticMode } = useFormEditPage();
	const { selectedLocations, handleLocationsUpdate } = useFormLocations();
	const [open, setOpen] = useState(false);

	return (
		<LocationPickerPopover
			open={open}
			onOpenChange={setOpen}
			selectedLocations={selectedLocations}
			onLocationsChange={handleLocationsUpdate}
			text={text}
			color={color}
			isStaticMode={isStaticMode}
		/>
	);
}

type LocationPickerPopoverProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	selectedLocations: string[];
	onLocationsChange: (locations: string[]) => void;
	text: string;
	color: TemplateColor;
	tooltipText?: string;
	isStaticMode?: boolean;
};

/**
 * Self-contained location picker popover.
 * Owns the useLocationSearch hook internally — consumers only need to supply
 * the controlled open state, the current selections, and a change handler.
 */
export function LocationPickerPopover({
	open,
	onOpenChange,
	selectedLocations,
	onLocationsChange,
	text,
	color,
	tooltipText,
	isStaticMode,
}: LocationPickerPopoverProps) {
	const {
		search,
		setSearch,
		trimmedSearch,
		citySearchResults,
		filteredRecent,
		showCustomAdd,
		handleSelectCity,
	} = useLocationSearch({ selectedLocations, onLocationsChange, isStaticMode });

	const listRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [activeIndex, setActiveIndex] = useState<number>(-1);

	// Focus the search input when the popover opens
	useEffect(() => {
		if (open) {
			// rAF ensures the popover content is mounted before we focus
			const id = requestAnimationFrame(() => inputRef.current?.focus());
			return () => cancelAnimationFrame(id);
		}
	}, [open]);

	// Flat list of all selectable items for keyboard navigation
	const allItems = useMemo(() => {
		const items: string[] = [];
		if (showCustomAdd) items.push(trimmedSearch);
		filteredRecent.forEach((c) => items.push(c));
		citySearchResults.forEach((c) => items.push(c));
		return items;
	}, [showCustomAdd, trimmedSearch, filteredRecent, citySearchResults]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === KeyboardKeys.ArrowDown) {
				e.preventDefault();
				setActiveIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0));
			} else if (e.key === KeyboardKeys.ArrowUp) {
				e.preventDefault();
				setActiveIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1));
			} else if (e.key === KeyboardKeys.Enter) {
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < allItems.length) {
					handleSelectCity(allItems[activeIndex]);
					setActiveIndex(-1);
				} else if (showCustomAdd) {
					handleSelectCity(trimmedSearch);
				}
			} else if (e.key === KeyboardKeys.Escape) {
				onOpenChange(false);
			}
		},
		[allItems, activeIndex, handleSelectCity, showCustomAdd, trimmedSearch, onOpenChange]
	);

	// Scroll the active item into view whenever activeIndex changes (including wrap-around)
	useEffect(() => {
		if (activeIndex < 0 || !listRef.current) return;
		const items = listRef.current.querySelectorAll<HTMLElement>('[data-item-index]');
		items[activeIndex]?.scrollIntoView({ block: 'nearest' });
	}, [activeIndex]);

	// Reset active index when list changes
	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(e.target.value);
			setActiveIndex(-1);
		},
		[setSearch]
	);

	// Compute flat item index offset helpers
	const customAddOffset = showCustomAdd ? 1 : 0;
	const recentOffset = customAddOffset + filteredRecent.length;

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			modal={true}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="cursor-pointer"
				>
					<FormWidget
						widgetType="LOCATION"
						text={text}
						color={color}
						tooltipText={tooltipText}
						variant="minimal"
						className="cursor-pointer hover:opacity-70"
					/>
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[260px] p-0"
				align="start"
			>
				<div className="flex flex-col">
					<div className="flex items-center border-b px-3">
						<Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
						<input
							ref={inputRef}
							className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
							placeholder="Search locations..."
							value={search}
							onChange={handleSearchChange}
							onKeyDown={handleKeyDown}
						/>
					</div>
					{showCustomAdd ||
					filteredRecent?.length > 0 ||
					(citySearchResults.length > 0 && !!trimmedSearch) ? (
						<div
							ref={listRef}
							className="max-h-[250px] overflow-y-auto p-1"
						>
							{showCustomAdd && (
								<button
									type="button"
									data-item-index={0}
									className={cn(
										'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
										activeIndex === 0 && 'bg-accent'
									)}
									onClick={() => handleSelectCity(trimmedSearch)}
								>
									<MapPin className="h-3.5 w-3.5" />
									Add &quot;{trimmedSearch}&quot;
								</button>
							)}
							{filteredRecent.length > 0 && (
								<>
									<p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Recent</p>
									{filteredRecent.map((city, i) => {
										const isSelected = selectedLocations.includes(city);
										const itemIndex = customAddOffset + i;
										return (
											<button
												key={`recent-${city}`}
												type="button"
												data-item-index={itemIndex}
												className={cn(
													'flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
													activeIndex === itemIndex && 'bg-accent'
												)}
												onClick={() => handleSelectCity(city)}
											>
												<Check
													className={cn(
														'mr-2 h-3.5 w-3.5',
														isSelected ? 'opacity-100' : 'opacity-0'
													)}
												/>
												{city}
											</button>
										);
									})}
								</>
							)}
							{citySearchResults.length > 0 && trimmedSearch && (
								<>
									<p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Locations</p>
									{citySearchResults.map((city, i) => {
										const isSelected = selectedLocations.includes(city);
										const itemIndex = recentOffset + i;
										return (
											<button
												key={city}
												type="button"
												data-item-index={itemIndex}
												className={cn(
													'flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
													activeIndex === itemIndex && 'bg-accent'
												)}
												onClick={() => handleSelectCity(city)}
											>
												<Check
													className={cn(
														'mr-2 h-3.5 w-3.5',
														isSelected ? 'opacity-100' : 'opacity-0'
													)}
												/>
												{city}
											</button>
										);
									})}
								</>
							)}
							{trimmedSearch &&
								citySearchResults.length === 0 &&
								filteredRecent.length === 0 &&
								!showCustomAdd && (
									<p className="py-3 text-center text-sm text-muted-foreground">
										No locations found.
									</p>
								)}
						</div>
					) : null}
				</div>
			</PopoverContent>
		</Popover>
	);
}
